import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Check, Minus } from 'lucide-react'
import { useI18n } from '../../i18n'
import type { Plan, FeatureKey } from '../../types'

const featureLabels: Record<FeatureKey, { zh: string; en: string }> = {
  chat: { zh: '基础AI对话', en: 'Basic AI Chat' },
  rag: { zh: '知识库问答', en: 'Knowledge Base Q&A' },
  multimodal: { zh: '多模态对话', en: 'Multimodal Chat' },
  deepseek: { zh: 'DeepSeek推理', en: 'DeepSeek Reasoning' },
  priority: { zh: '优先技术支持', en: 'Priority Support' },
  topup: { zh: 'Token加购', en: 'Token Top-up' },
}

const popularPlans: string[] = ['PRO']

interface PricingTierCardProps {
  plan: Plan
  onSelect: (plan: Plan) => void
  isCurrent?: boolean
}

function formatPrice(price: number, lang: string) {
  if (price === 0) return lang === 'zh' ? '免费' : 'Free'
  return `¥${price.toLocaleString()}`
}

function formatTokenQuota(n: number, lang: string) {
  if (n >= 10000000) return lang === 'zh' ? `${(n / 10000000).toFixed(0)}千万` : `${(n / 1000000).toFixed(0)}M`
  if (n >= 10000) return lang === 'zh' ? `${(n / 10000).toFixed(0)}万` : `${(n / 1000).toFixed(0)}K`
  return n.toLocaleString()
}

function parseFeatures(featuresStr: string): FeatureKey[] {
  try {
    return JSON.parse(featuresStr)
  } catch {
    return []
  }
}

export function PricingTierCard({ plan, onSelect, isCurrent }: PricingTierCardProps) {
  const { lang, t } = useI18n()
  const isPopular = popularPlans.includes(plan.planCode)
  const isTopup = plan.planCode.startsWith('TOPUP')
  const isFree = plan.planCode === 'FREE'
  const features = parseFeatures(plan.features)

  return (
    <div
      className={`relative flex flex-col p-6 rounded-2xl bg-white border transition-all duration-300 hover:shadow-lg ${
        isPopular
          ? 'border-orange-200 shadow-md ring-2 ring-orange-100'
          : 'border-black/[0.04] hover:border-orange-100'
      }`}
    >
      {isPopular && (
        <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-orange-500 text-white border-0 text-[10px] px-3 py-0.5">
          {t('最受欢迎', 'Most Popular')}
        </Badge>
      )}

      <div className="mb-4">
        <h3 className="text-lg font-semibold text-[#1a1a1a]">{plan.name}</h3>
        <p className="text-xs text-gray-400 mt-1">
          {isTopup
            ? t('Token加购包', 'Token Top-up Pack')
            : plan.maxUsers > 0
            ? t(`约${plan.maxUsers}人使用`, `~${plan.maxUsers} users`)
            : t('不限人数', 'Unlimited users')
          }
        </p>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold text-[#1a1a1a]">{formatPrice(plan.priceMonthly, lang)}</span>
          {plan.priceMonthly > 0 && !isTopup && (
            <span className="text-sm text-gray-400">/{t('月', 'mo')}</span>
          )}
        </div>
        {plan.priceYearly !== null && plan.priceMonthly > 0 && !isTopup && (
          <p className="text-xs text-emerald-500 mt-1">
            {t(`年付 ¥${plan.priceYearly.toLocaleString()}，更优惠`, `¥${plan.priceYearly.toLocaleString()}/yr, save more`)}
          </p>
        )}
      </div>

      {!isTopup && (
        <div className="flex items-center gap-2 mb-4 p-2.5 rounded-xl bg-orange-50/50">
          <span className="text-xs text-orange-600 font-medium">
            {formatTokenQuota(plan.tokenQuota, lang)} tokens/{t('月', 'mo')}
          </span>
        </div>
      )}

      {plan.description && (
        <p className="text-xs text-gray-400 mb-4">{plan.description}</p>
      )}

      <ul className="space-y-2.5 mb-6 flex-1">
        {features.map((key) => {
          const label = featureLabels[key]
          if (!label) return null
          return (
            <li key={key} className="flex items-start gap-2 text-sm text-gray-600">
              <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>{t(label.zh, label.en)}</span>
            </li>
          )
        })}
      </ul>

      <Button
        onClick={() => !isCurrent && onSelect(plan)}
        disabled={isCurrent}
        className={`w-full h-10 text-sm font-medium rounded-xl ${
          isPopular
            ? 'bg-orange-500 text-white hover:bg-orange-600'
            : 'bg-[#1a1a1a] text-white hover:bg-[#333]'
        }`}
      >
        {isCurrent
          ? t('当前套餐', 'Current Plan')
          : isFree
          ? t('免费开始', 'Get Started Free')
          : isTopup
          ? t('立即加购', 'Buy Now')
          : t('选择套餐', 'Choose Plan')}
      </Button>
    </div>
  )
}
